import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { api } from "../lib/api";
import { Header } from "../components/public/Header";
import { Footer } from "../components/public/Footer";
import { ProductCard } from "../components/public/ProductCard";
import { ProductModal } from "../components/public/ProductModal";

const HERO_IMG =
  "https://customer-assets.emergentagent.com/job_thread-gallery-21/artifacts/x3u0cva3_79741848-742d-43b4-bd13-0c8fe250f09f.JPG";
const ABOUT_IMG =
  "https://customer-assets.emergentagent.com/job_thread-gallery-21/artifacts/cb639rez_6da20111-401e-49a8-88d0-d2bb5cb02c13.JPG";

const marqueeWords = ["Elegance", "Soul", "Tailoring", "Curated", "Luxury", "VeeVee's"];

export default function Home() {
  const [settings, setSettings] = useState(null);
  const [featured, setFeatured] = useState([]);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
    api.get("/products", { params: { featured: true } }).then((r) => setFeatured(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (window.location.hash) {
      const el = document.querySelector(window.location.hash);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), 300);
    }
  }, [settings]);

  return (
    <div className="bg-obsidian min-h-screen">
      <Header settings={settings} />

      {/* HERO */}
      <section className="relative min-h-screen w-full overflow-hidden bg-obsidian flex items-center pt-28 pb-16">
        <div className="absolute inset-0 opacity-[0.06] pointer-events-none"
             style={{ backgroundImage: "radial-gradient(circle at 30% 20%, #D4AF37 0%, transparent 45%)" }} />
        <div className="relative z-10 w-full max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div>
            <p className="font-accent text-xs tracking-[0.4em] uppercase text-champagne fade-up">
              {settings?.tagline || "VeeVee's"} — Fashion House
            </p>
            <h1 className="font-heading text-5xl sm:text-7xl lg:text-[5.5rem] text-ink leading-[0.95] mt-5 fade-up" style={{ animationDelay: "120ms" }}>
              {settings?.brand_name || "Trendy threads by Vee"}
            </h1>
            <p className="text-ash text-base sm:text-lg max-w-md mt-7 leading-relaxed fade-up" style={{ animationDelay: "240ms" }}>
              Threads woven with soul. Hand-dyed batik pieces and curated silhouettes for those who dress with intention.
            </p>
            <div className="flex flex-wrap gap-4 mt-10 fade-up" style={{ animationDelay: "360ms" }}>
              <Link
                to="/collections"
                data-testid="hero-shop-cta"
                className="group inline-flex items-center gap-2 bg-champagne text-obsidian font-accent text-xs tracking-[0.2em] uppercase px-8 py-4 hover:bg-champagne-hover transition-colors duration-300"
              >
                View Collections
                <ArrowUpRight size={16} className="group-hover:rotate-45 transition-transform duration-300" />
              </Link>
              <a
                href="#about"
                className="inline-flex items-center gap-2 border border-white/20 text-ink font-accent text-xs tracking-[0.2em] uppercase px-8 py-4 hover:border-champagne hover:text-champagne transition-all duration-300"
              >
                The Story
              </a>
            </div>
          </div>

          <div className="relative fade-up" style={{ animationDelay: "300ms" }}>
            <div className="absolute -inset-3 border border-champagne/30 hidden sm:block" />
            <div className="relative aspect-[3/4] max-w-md mx-auto lg:ml-auto overflow-hidden bg-obsidian-2">
              <img src={HERO_IMG} alt="Featured piece" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>


      {/* MARQUEE */}
      <div className="border-y border-white/10 py-5 overflow-hidden bg-obsidian-2">
        <div className="flex marquee-track w-max">
          {[...marqueeWords, ...marqueeWords, ...marqueeWords, ...marqueeWords].map((w, i) => (
            <span key={i} className="font-heading text-2xl text-ash/40 px-8 flex items-center gap-8">
              {w} <span className="text-champagne text-xs">✦</span>
            </span>
          ))}
        </div>
      </div>

      {/* FEATURED */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-24 sm:py-32">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-14">
          <div>
            <p className="font-accent text-xs tracking-[0.3em] uppercase text-champagne">Selected Pieces</p>
            <h2 className="font-heading text-4xl sm:text-5xl text-ink mt-3">Featured Collection</h2>
          </div>
          <Link to="/collections" className="font-accent text-xs tracking-[0.2em] uppercase text-ash hover:text-champagne transition-colors inline-flex items-center gap-2">
            View all <ArrowUpRight size={14} />
          </Link>
        </div>

        {featured.length === 0 ? (
          <p className="text-ash">New pieces arriving soon.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-14">
            {featured.map((p, i) => (
              <ProductCard key={p.id} product={p} onClick={setSelected} index={i} />
            ))}
          </div>
        )}
      </section>

      {/* ABOUT */}
      <section id="about" className="bg-obsidian-2 border-y border-white/10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24 sm:py-32 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div className="relative aspect-[4/5] overflow-hidden border border-white/5">
            <img src={ABOUT_IMG} alt="About the brand" className="w-full h-full object-cover" />
          </div>
          <div>
            <p className="font-accent text-xs tracking-[0.3em] uppercase text-champagne">Our Story</p>
            <h2 className="font-heading text-4xl sm:text-6xl text-ink mt-4 leading-tight">
              Dressing with intention
            </h2>
            <p className="text-ash leading-relaxed tracking-wide mt-8 text-base sm:text-lg">
              {settings?.about_text ||
                "Trendy threads by Vee is a curated fashion house built on the belief that what you wear should feel like an extension of who you are."}
            </p>
            <div className="grid grid-cols-3 gap-6 mt-12">
              {[
                ["Hand", "Curated edits"],
                ["Soul", "Woven into every thread"],
                ["You", "Dressed with intention"],
              ].map(([k, v]) => (
                <div key={k}>
                  <p className="font-heading text-3xl text-champagne">{k}</p>
                  <p className="text-ash text-xs tracking-wide mt-2">{v}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Footer settings={settings} />
      <ProductModal product={selected} settings={settings} onClose={() => setSelected(null)} />
    </div>
  );
}
